import { useCallback, useEffect, useState } from "react";
import { LayerResults, ReviewActions, CompletePresencial } from "../../components/kyc/LayerResults";
import { ScreeningResults } from "../../components/screening/ScreeningResults";
import { api } from "../../lib/api";
import { fmtFecha } from "../../lib/format";
import { DOC_TIPOS, PROPOSITOS, MONTOS, PEP_OPCIONES, paisNombre } from "../../lib/kycData";
import { ORIGEN_FONDOS } from "../../components/kyc/StepDatos";
import { useAuth } from "../../context/AuthContext";
import { PageHeader } from "../../components/PageHeader";
import { StatusBadge } from "../../components/StatusBadge";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

const L = (arr, v) => Object.fromEntries(arr)[v] || v;
const EST = { iniciada: "pendiente_verificacion", enviada: "ok", vencida: "fallido" };

const Media = ({ f }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let u;
    api.get(`/company/kyc/archivos/${f.id}`, { responseType: "blob" }).then((r) => {
      u = URL.createObjectURL(r.data);
      setUrl(u);
    });
    return () => u && URL.revokeObjectURL(u);
  }, [f.id]);
  return (
    <figure className="space-y-1">
      {url && (f.content_type.startsWith("video") ? <video src={url} controls playsInline className="w-full rounded-lg bg-black" /> : <img src={url} alt={f.tipo} className="w-full rounded-lg border" />)}
      <figcaption className="text-xs text-slate-500">{f.tipo} · {Math.round(f.size / 1024)} KB{f.meta?.calidad ? ` · nitidez ${f.meta.calidad.nitidez}` : ""}</figcaption>
    </figure>
  );
};

const Detail = ({ sid, onClose, rol }) => {
  const [s, setS] = useState(null);
  const load = useCallback(() => {
    if (sid) api.get(`/company/kyc/sesiones/${sid}`).then((r) => setS(r.data));
    else setS(null);
  }, [sid]);
  useEffect(() => {
    load();
  }, [load]);
  const d = s?.datos;
  return (
    <Dialog open={!!sid} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="kyc-detail-dialog">
        <DialogHeader>
          <DialogTitle>{d ? `${d.nombres} ${d.apellido_paterno} ${d.apellido_materno || ""}` : "Verificación"}</DialogTitle>
          <DialogDescription>{s && `${s.canal} · inicio ${fmtFecha(s.inicio)} · vence ${fmtFecha(s.vencimiento)}`}</DialogDescription>
        </DialogHeader>
        {s && (
          <div className="space-y-4 text-sm">
            {d && (
              <div className="grid sm:grid-cols-2 gap-2">
                <p><b>Documento:</b> {L(DOC_TIPOS, d.documento_tipo)} {d.documento_numero} (vence {d.documento_vencimiento})</p>
                <p><b>Nacimiento:</b> {d.fecha_nacimiento} · {paisNombre(d.nacionalidad)}</p>
                <p><b>Domicilio:</b> {d.domicilio.calle} {d.domicilio.numero}, {d.domicilio.comuna}</p>
                <p><b>Contacto:</b> {d.telefono} · {d.correo}</p>
                <p><b>Ocupación:</b> {d.ocupacion_otro || d.ocupacion}</p>
                <p><b>Propósito:</b> {L(PROPOSITOS, d.proposito)} · {L(MONTOS, d.monto_rango)}{d.origen_fondos ? ` · Origen de fondos: ${L(ORIGEN_FONDOS, d.origen_fondos)}` : ""}</p>
                <p><b>PEP:</b> {L(PEP_OPCIONES, d.pep.tipo)}{d.pep.cargo ? ` · ${d.pep.cargo} (${d.pep.institucion})` : ""}</p>
              </div>
            )}
            <p><b>Consentimientos:</b> {s.consentimientos.map((c) => `${c.casilla}: ${c.aceptada ? "sí" : "no"}`).join(" · ") || "—"}</p>
            <p><b>Señales:</b> {s.senales.map((x) => x.codigo).join(", ") || "ninguna"}</p>
            <p className="break-all"><b>Dispositivo:</b> IP {s.dispositivo.ip} · {s.dispositivo.user_agent}</p>
            <p><b>Duración por paso (s):</b> {Object.entries(s.pasos).map(([k, v]) => `${k}: ${v.duracion_cliente_s ?? "—"}`).join(" · ")}</p>
            {s.verificacion_presencial && <p><b>Verificación presencial:</b> {s.verificacion_presencial.nombre} · {fmtFecha(s.verificacion_presencial.fecha)}</p>}
            <LayerResults s={s} />
            <ReviewActions s={s} rol={rol} onDone={load} />
            {s.client_id && <ScreeningResults clientId={s.client_id} />}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{s.archivos.map((f) => <Media key={f.id} f={f} />)}</div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default function Verificaciones() {
  const { me } = useAuth();
  const [items, setItems] = useState([]);
  const [sel, setSel] = useState(null);
  const [pres, setPres] = useState(null);
  const canSee = ["owner", "compliance_officer"].includes(me.usuario.rol) || me.soporte;
  const load = useCallback(() => api.get("/company/kyc/sesiones").then((r) => setItems(r.data.items)), []);
  useEffect(() => {
    load();
  }, [load]);
  return (
    <div className="space-y-8">
      <PageHeader overline="Clientes" title="Verificaciones KYC" />
      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <Table data-testid="kyc-sessions-table">
          <TableHeader><TableRow>{["Inicio", "Cliente", "Documento", "Canal", "Estado", "Resultado KYC", "Señales", ""].map((h) => <TableHead key={h}>{h}</TableHead>)}</TableRow></TableHeader>
          <TableBody>
            {items.map((s) => (
              <TableRow key={s.id} data-testid={`kyc-row-${s.id}`}>
                <TableCell className="text-xs whitespace-nowrap">{fmtFecha(s.inicio)}</TableCell>
                <TableCell>{s.datos ? `${s.datos.nombres} ${s.datos.apellido_paterno} ${s.datos.apellido_materno || ""}` : "—"}</TableCell>
                <TableCell className="font-mono text-xs">{s.datos?.documento_numero || "—"}</TableCell>
                <TableCell className="text-sm">{s.canal}</TableCell>
                <TableCell><StatusBadge value={EST[s.estado]} label={s.estado} /></TableCell>
                <TableCell className="text-xs" data-testid={`kyc-result-${s.id}`}>{s.resultado_kyc ? `${s.resultado_kyc} · ${s.puntaje}` : s.estado_proceso || "—"}</TableCell>
                <TableCell className="text-xs text-rose-700">{s.senales.map((x) => x.codigo).join(", ")}</TableCell>
                <TableCell className="flex gap-2">
                  {canSee && <Button size="sm" variant="outline" onClick={() => setSel(s.id)} data-testid={`kyc-view-${s.id}`}>Ver</Button>}
                  {s.requiere_presencial && !me.soporte && <Button size="sm" onClick={() => setPres(s.id)} data-testid={`kyc-complete-presencial-${s.id}`}>Completar presencial</Button>}
                </TableCell>
              </TableRow>
            ))}
            {!items.length && <TableRow><TableCell colSpan={8} className="text-center text-slate-500 py-10">Aún no hay verificaciones</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
      <Detail sid={sel} rol={me.usuario.rol} onClose={() => setSel(null)} />
      <Dialog open={!!pres} onOpenChange={(v) => !v && setPres(null)}>
        <DialogContent data-testid="complete-presencial-dialog">
          <DialogHeader><DialogTitle>Completar verificación presencial</DialogTitle><DialogDescription>La persona no aceptó el uso de su imagen facial. Compara su cara con el documento en persona.</DialogDescription></DialogHeader>
          {pres && <CompletePresencial sid={pres} operador={me.usuario.nombre} onDone={() => { setPres(null); load(); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
